import { useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import AccordionMenu from '../../../common/accordion-menu';
import Switch from '../../../common/switch';
import TooltipIcon from '../../../common/tooltip-icon';
import Loading from '../../../common/loading';
import l from '../../../../lang';
import { getCountryFlagUrl, getLanguage } from '../../../../utils';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import { canToggleThreeDSecure } from '../../../../store/payment-methods/roles';
import { ThreeDSecureCountryInfo, useThreeDSecureInfo } from '../../../../hooks/use-threed-secure-info';
import { countriesByRegions } from '../../../../config';
import { Urls } from '../../../../config/urls';
import './styles.scss';

const REGIONS = [
  { id: 'LA', titleKey: 'region.LA' },
  { id: 'AF_ME', titleKey: 'region.AF_ME' },
  { id: 'AS', titleKey: 'region.AS' },
];

const formatCost = (cost: number | null | undefined) => {
  if (cost == null || Number(cost) === 0) {
    return '-';
  }
  return `USD ${Number(cost).toFixed(2)}`;
};

interface ThreeDSecureTableProps {
  rows: ThreeDSecureCountryInfo[];
  canToggle: boolean;
  onToggle: (country: string, enabled: boolean) => void;
}

const ThreeDSecureTable = ({ rows, canToggle, onToggle }: ThreeDSecureTableProps) => (
  <div className="three-d-secure__table-panel">
    <table className="three-d-secure__table">
      <thead>
        <tr>
          <th>{l('paymentMethod.installments.header.country')}</th>
          <th>{l('paymentMethod.threedSecure.header.available')}</th>
          <th>
            <span className="three-d-secure__header-with-icon">
              {l('paymentMethod.installments.header.cost')}
              <TooltipIcon
                reducedPadding
                placement="bottom"
                text={l('paymentMethod.threedSecure.costTooltip')}
                icon={<InfoIcon className="three-d-secure__info-icon" aria-hidden />}
              />
            </span>
          </th>
          <th>{l('paymentMethod.threedSecure.header.enabled')}</th>
          <th>{l('paymentMethod.threedSecure.header.coverage')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.country}>
            <td>
              <div className="three-d-secure__country">
                <img alt="" className="three-d-secure__country-flag" src={getCountryFlagUrl(row.country)} />
                <span>{l(`countryCode.${row.country}`)}</span>
              </div>
            </td>
            <td>{row.available ? l('common.yes') : l('common.no')}</td>
            <td>{formatCost(row.cost)}</td>
            <td>
              <div className="three-d-secure__toggle">
                {row.forced ? (
                  <Tooltip title={l('paymentMethod.threedSecure.forcedTooltip')} arrow placement="top">
                    <span className="three-d-secure__toggle--locked">
                      <Switch checked={Boolean(row.available && row.enabled)} onChange={() => undefined} disabled={false} />
                    </span>
                  </Tooltip>
                ) : (
                  <Switch
                    checked={Boolean(row.available && row.enabled)}
                    onChange={() => onToggle(row.country, !row.enabled)}
                    disabled={!row.available || !canToggle}
                  />
                )}
              </div>
            </td>
            <td>{l(`paymentMethod.threedSecure.coverage.${row.coverage}`)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function ThreeDSecure() {
  const { data, loading, error, toggleCountry } = useThreeDSecureInfo();
  const { auth } = useSelector((state: { auth?: { roles?: string[] } }) => state);
  const canToggle = canToggleThreeDSecure(auth?.roles);

  const rowsByRegion = useMemo(() => {
    const assigned = new Set<string>();
    const grouped: Record<string, ThreeDSecureCountryInfo[]> = {};
    REGIONS.forEach(region => {
      const codes = countriesByRegions[region.id as keyof typeof countriesByRegions] || [];
      grouped[region.id] = codes
        .map(code => data.find(row => row.country === code))
        .filter((row): row is ThreeDSecureCountryInfo => Boolean(row));
      codes.forEach(code => assigned.add(code));
    });
    const extras = data.filter(row => !assigned.has(row.country));
    if (extras.length) {
      grouped.LA = [...(grouped.LA || []), ...extras];
    }
    return grouped;
  }, [data]);

  const items = REGIONS.map(region => {
    const rows = rowsByRegion[region.id] || [];
    return {
      id: region.id,
      title: l(region.titleKey),
      disabled: rows.length === 0,
      content: rows.length ? (
        <ThreeDSecureTable rows={rows} canToggle={canToggle} onToggle={toggleCountry} />
      ) : null,
    };
  });

  return (
    <div className="three-d-secure">
      <div className="payment-methods__subtitle">{l('paymentMethod.threedSecure.byCountry')}</div>
      {loading && (
        <div className="three-d-secure__loading">
          <Loading />
        </div>
      )}
      {error && !loading && <div className="three-d-secure__error">{l('common.error')}</div>}
      {!loading && !error && (
        <>
          <div className="three-d-secure__content">
            <AccordionMenu items={items} />
          </div>
          <div className="three-d-secure__footer">
            <span>{l('paymentMethod.threedSecure.footer')}</span>{' '}
            <a
              className="three-d-secure__see-more"
              href={Urls.threeDSecureHelp[getLanguage()]}
              target="_blank"
              rel="noopener noreferrer"
            >
              {l('paymentMethod.threedSecure.header.seeMore')}
            </a>
          </div>
        </>
      )}
    </div>
  );
}

export default ThreeDSecure;
